import { useEffect, useRef } from "react"
import type { ChartOption, ThemeMode } from "@csbi/viz-core"

type TooltipFormatter = NonNullable<NonNullable<ChartOption["tooltip"]>["formatter"]>
import { ensureEchartsRegistered, echarts } from "./echarts-setup.js"

export type EChartsBaseProps = {
  option: ChartOption
  height?: number | string
  mode: ThemeMode
  ariaLabel: string
  dimmed?: boolean
  stale?: boolean
  onSeriesToggle?: (name: string, visible: boolean) => void
  /**
   * ⚠ `category` is **what the axis shows**, which is not always what the data holds — a user axis shows names
   * and the rows hold pseudonyms. `dataIndex` is the exact position, and the only reliable way back to the raw
   * value; two people with the same name would otherwise both resolve to the first one.
   */
  onMarkClick?: (payload: { seriesName: string; category: string; value: number | null; dataIndex: number }) => void
  onGeoRoam?: (view: { zoom: number; center?: [number, number] }) => void
}

type EChartsInstance = ReturnType<typeof echarts.init>

export function EChartsBase({
  option,
  height = "100%",
  mode,
  ariaLabel,
  dimmed = false,
  stale = false,
  onSeriesToggle,
  onMarkClick,
  onGeoRoam,
}: EChartsBaseProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<EChartsInstance | null>(null)
  // Callbacks live in a ref: otherwise a new function on every parent render means rebinding the events.
  // The write has to happen inside an effect — mutating a ref during render makes "what this render saw" undefined
  const handlers = useRef({ onSeriesToggle, onMarkClick, onGeoRoam })
  useEffect(() => {
    handlers.current = { onSeriesToggle, onMarkClick, onGeoRoam }
  }, [onSeriesToggle, onMarkClick, onGeoRoam])

  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    ensureEchartsRegistered()
    const chart = echarts.init(host, undefined, { renderer: "canvas", locale: "EN" })
    chartRef.current = chart

    chart.on("legendselectchanged", (raw: unknown) => {
      const e = raw as { name: string; selected: Record<string, boolean> }
      handlers.current.onSeriesToggle?.(e.name, e.selected[e.name] !== false)
    })
    chart.on("click", (raw: unknown) => {
      const e = raw as { seriesName?: string; name?: string; value?: unknown; dataIndex?: number }
      handlers.current.onMarkClick?.({
        seriesName: e.seriesName ?? "",
        category: e.name ?? "",
        value: typeof e.value === "number" ? e.value : null,
        dataIndex: typeof e.dataIndex === "number" ? e.dataIndex : -1,
      })
    })

    chart.on("georoam", () => {
      const series = (chart.getOption() as { series?: { zoom?: number; center?: [number, number] }[] }).series
      const view = series?.[0]
      if (view?.zoom != null)
        handlers.current.onGeoRoam?.({ zoom: view.zoom, ...(view.center ? { center: view.center } : {}) })
    })

    const observer = new ResizeObserver(() => chart.resize())
    observer.observe(host)

    return () => {
      observer.disconnect()
      chart.dispose()
      chartRef.current = null
    }
  }, [mode])

  /**
   * ⚠ **ECharts puts a pointer cursor on every mark, whether or not anything listens for the click.**
   */
  const clickable = Boolean(onMarkClick)
  useEffect(() => {
    /**
     * ⚠ **The cursor alone is not an affordance.** It was the first attempt, and the honest report back was
     * that a drillable chart and a dead one look identical on hover — which is true: a pointer is only visible
     * by comparison with something else, and nobody hovers two charts side by side to find out. So the tooltip,
     * which is already open at exactly that moment, says what the click will do.
     */
    const tooltip = option.tooltip
    const hinted =
      clickable && typeof tooltip?.formatter === "function"
        ? {
            ...tooltip,
            formatter: ((p: never) => {
              const body = (tooltip.formatter as (x: never) => string)(p)
              return body
                ? `${body}<div style="margin-top:6px;font-size:11px;opacity:.6">Click to open this over time →</div>`
                : body
            }) as TooltipFormatter,
          }
        : tooltip

    const decorated = {
      ...option,
      ...(hinted ? { tooltip: hinted } : {}),
      ...(Array.isArray((option as { series?: unknown }).series)
        ? {
            series: ((option as { series: Record<string, unknown>[] }).series).map((s) => ({
              ...s,
              cursor: clickable ? "pointer" : "default",
            })),
          }
        : {}),
    }
    // notMerge: true — with fewer series, merge would leave the previous render's ghosts behind
    chartRef.current?.setOption(decorated as Parameters<EChartsInstance["setOption"]>[0], {
      notMerge: true,
    })
  }, [option, clickable])

  return (
    <div
      ref={hostRef}
      role="img"
      aria-label={ariaLabel}
      className={stale ? "csbi-chart csbi-chart--stale" : "csbi-chart"}
      style={{
        height,
        width: "100%",
        opacity: dimmed || stale ? 0.6 : 1,
        transition: "opacity 160ms linear",
      }}
    />
  )
}
